import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Button } from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../../request/http-common';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { CardActions } from '@mui/material';

const emptyForm = {
  name: '',
  max_amount: '',
  min_amount: '',
  bonus_per_downline: '',
  number_of_downlines: '',
  total_earning: '',
  initial_earning: '',
  duration: '',
  amount_per_hour: '',
  category: '',
};

function normalizeFieldErrors(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const raw = payload.errors;
  if (!raw || typeof raw !== 'object') {
    const msg = payload.message;
    return msg ? { _general: String(msg) } : {};
  }
  const out = {};
  Object.keys(raw).forEach((key) => {
    const v = raw[key];
    out[key] = Array.isArray(v) ? v[0] : v;
  });
  return out;
}

function loadErrorMessage(err) {
  const d = err?.response?.data;
  if (typeof d?.message === 'string') return d.message;
  if (d?.errors && typeof d.errors === 'object') {
    const first = Object.values(d.errors)[0];
    if (Array.isArray(first) && first[0]) return String(first[0]);
  }
  if (err?.message) return err.message;
  return 'Something went wrong while loading this plan.';
}

const EditPlan = () => {
  const params = useParams();
  const planId = params.id;

  const [fieldErrors, setFieldErrors] = useState({});
  const [loadError, setLoadError] = useState('');
  const [planForm, setPlanForm] = useState(emptyForm);

  const {
    data: planPayload,
    isLoading: planLoading,
    isError: planIsError,
    error: planError,
    refetch: refetchPlan,
    isFetching: planFetching,
  } = useQuery(
    ['edit-plan', planId],
    async () => {
      const res = await apiClient.get(`/api/edit-plan/${planId}`);
      if (res.data?.status !== 'success' || !res.data?.plan) {
        const msg = res.data?.message || 'Unable to load plan.';
        throw new Error(msg);
      }
      return res.data.plan;
    },
    {
      enabled: Boolean(planId),
      retry: 1,
      onError: (err) => {
        setLoadError(loadErrorMessage(err));
      },
      onSuccess: () => {
        setLoadError('');
      },
    }
  );

  const { data: categories, isLoading: categoriesLoading } = useQuery(
    ['categories', 'edit-plan-page'],
    async () => {
      const res = await apiClient.get('/api/categories');
      if (res.data?.status === 'success' && Array.isArray(res.data.categories)) {
        return res.data.categories;
      }
      return [];
    },
    { retry: 1 }
  );

  useEffect(() => {
    if (!planPayload) return;
    const p = planPayload;
    setPlanForm({
      name: p.name != null ? String(p.name) : '',
      max_amount: p.amount_invested != null ? String(p.amount_invested) : '',
      min_amount: p.min_amount != null ? String(p.min_amount) : '',
      bonus_per_downline: p.bonus_per_downline != null ? String(p.bonus_per_downline) : '',
      number_of_downlines: p.number_of_downlines != null ? String(p.number_of_downlines) : '',
      total_earning: p.total_earning != null ? String(p.total_earning) : '',
      initial_earning: p.initial_earning != null ? String(p.initial_earning) : '',
      duration: p.duration != null ? String(p.duration) : '',
      amount_per_hour: p.amount_per_hour != null ? String(p.amount_per_hour) : '',
      category: p.category_id != null && p.category_id !== '' ? String(p.category_id) : '',
    });
    setFieldErrors({});
  }, [planPayload]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setPlanForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const { isLoading: isSendingRequest, mutate: postPlan } = useMutation(
    async () => {
      return await apiClient.post(`/api/update-plan/${planId}`, {
        name: planForm.name,
        max_amount: planForm.max_amount,
        min_amount: planForm.min_amount,
        bonus_per_downline: planForm.bonus_per_downline,
        number_of_downlines: planForm.number_of_downlines,
        total_earning: planForm.total_earning,
        initial_earning: planForm.initial_earning,
        duration: planForm.duration,
        amount_per_hour: planForm.amount_per_hour,
        category: planForm.category,
      });
    },
    {
      onSuccess: (res) => {
        setFieldErrors({});
        if (res.data?.status === 'success') {
          localStorage.setItem(
            'notification',
            JSON.stringify({ message: res.data.message || 'Plan updated.', type: 'success' })
          );
          window.dispatchEvent(new Event('newMessage'));
        } else {
          localStorage.setItem(
            'notification',
            JSON.stringify({
              message: res.data?.message || 'Update did not complete.',
              type: 'error',
            })
          );
          window.dispatchEvent(new Event('newMessage'));
        }
        window.dispatchEvent(new Event('processed'));
        refetchPlan();
      },
      onError: (err) => {
        const d = err.response?.data;
        setFieldErrors(normalizeFieldErrors(d));
        const msg =
          d?.message ||
          (typeof d === 'string' ? d : null) ||
          'Could not update the plan. Check the form and try again.';
        localStorage.setItem('notification', JSON.stringify({ message: msg, type: 'error' }));
        window.dispatchEvent(new Event('newMessage'));
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const onSubmit = () => {
    window.dispatchEvent(new Event('processing'));
    postPlan();
  };

  const showForm = Boolean(planPayload) && !planLoading;
  const blockingLoad = planLoading && !planPayload;

  return (
    <div style={{ marginTop: '70px' }}>
      <Grid container px={2} mt={4} mb={4} rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid item xs={12} md={12}>
          {!planId && (
            <Alert severity="warning" sx={{ m: 1 }}>
              Missing plan id in the URL.
            </Alert>
          )}

          {blockingLoad && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={240} flexDirection="column" gap={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading plan…
              </Typography>
            </Box>
          )}

          {planIsError && !blockingLoad && (
            <Alert
              severity="error"
              sx={{ m: 1 }}
              action={
                <Button color="inherit" size="small" onClick={() => refetchPlan()}>
                  Retry
                </Button>
              }
            >
              {loadError || loadErrorMessage(planError)}
            </Alert>
          )}

          {fieldErrors._general && (
            <Alert severity="error" sx={{ m: 1 }} onClose={() => setFieldErrors((e) => ({ ...e, _general: undefined }))}>
              {fieldErrors._general}
            </Alert>
          )}

          {showForm && (
            <Card sx={{ marginX: '5px', borderRadius: '9px', height: '100%' }}>
              <CardContent>
                <Typography
                  mt={2}
                  sx={{ fontWeight: 'bold', fontSize: 24, textAlign: 'center' }}
                  gutterBottom
                  variant="h6"
                  component="div"
                >
                  EDIT INVESTMENT PLAN
                </Typography>
                <Typography sx={{ textAlign: 'center', fontSize: 16 }} variant="subtitle1" gutterBottom>
                  Plan #{planPayload.id}
                  {planFetching ? ' · refreshing…' : ''}
                </Typography>
                <Typography sx={{ textAlign: 'center', fontSize: 14, mb: 2 }} color="text.secondary" variant="body2">
                  Update the fields below. All values are loaded from the server for this plan.
                </Typography>

                <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                  <InputLabel htmlFor="name">Plan Name</InputLabel>
                  <OutlinedInput
                    id="name"
                    name="name"
                    label="Plan Name"
                    value={planForm.name}
                    onChange={onChange}
                    error={Boolean(fieldErrors.name)}
                  />
                  {fieldErrors.name && (
                    <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                      {fieldErrors.name}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                  <InputLabel htmlFor="min_amount">Min Amount</InputLabel>
                  <OutlinedInput
                    id="min_amount"
                    name="min_amount"
                    label="Min Amount"
                    type="number"
                    value={planForm.min_amount}
                    onChange={onChange}
                    error={Boolean(fieldErrors.min_amount)}
                  />
                  {fieldErrors.min_amount && (
                    <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                      {fieldErrors.min_amount}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                  <InputLabel htmlFor="max_amount">Max Amount</InputLabel>
                  <OutlinedInput
                    id="max_amount"
                    name="max_amount"
                    label="Max Amount"
                    type="number"
                    value={planForm.max_amount}
                    onChange={onChange}
                    error={Boolean(fieldErrors.max_amount)}
                  />
                  {fieldErrors.max_amount && (
                    <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                      {fieldErrors.max_amount}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                  <InputLabel htmlFor="amount_per_hour">Amount Per Hour</InputLabel>
                  <OutlinedInput
                    id="amount_per_hour"
                    name="amount_per_hour"
                    label="Amount Per Hour"
                    type="number"
                    value={planForm.amount_per_hour}
                    onChange={onChange}
                    error={Boolean(fieldErrors.amount_per_hour)}
                  />
                  {fieldErrors.amount_per_hour && (
                    <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                      {fieldErrors.amount_per_hour}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                  <InputLabel htmlFor="number_of_downlines">Number Of Downlines</InputLabel>
                  <OutlinedInput
                    id="number_of_downlines"
                    name="number_of_downlines"
                    label="Number Of Downlines"
                    type="number"
                    value={planForm.number_of_downlines}
                    onChange={onChange}
                    error={Boolean(fieldErrors.number_of_downlines)}
                  />
                  {fieldErrors.number_of_downlines && (
                    <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                      {fieldErrors.number_of_downlines}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                  <InputLabel htmlFor="bonus_per_downline">Bonus Per Downline</InputLabel>
                  <OutlinedInput
                    id="bonus_per_downline"
                    name="bonus_per_downline"
                    label="Bonus Per Downline"
                    type="number"
                    value={planForm.bonus_per_downline}
                    onChange={onChange}
                    error={Boolean(fieldErrors.bonus_per_downline)}
                  />
                  {fieldErrors.bonus_per_downline && (
                    <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                      {fieldErrors.bonus_per_downline}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                  <InputLabel htmlFor="total_earning">Total Earning</InputLabel>
                  <OutlinedInput
                    id="total_earning"
                    name="total_earning"
                    label="Total Earning"
                    type="number"
                    value={planForm.total_earning}
                    onChange={onChange}
                    error={Boolean(fieldErrors.total_earning)}
                  />
                  {fieldErrors.total_earning && (
                    <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                      {fieldErrors.total_earning}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                  <InputLabel htmlFor="initial_earning">Initial Earning</InputLabel>
                  <OutlinedInput
                    id="initial_earning"
                    name="initial_earning"
                    label="Initial Earning"
                    type="number"
                    value={planForm.initial_earning}
                    onChange={onChange}
                    error={Boolean(fieldErrors.initial_earning)}
                  />
                  {fieldErrors.initial_earning && (
                    <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                      {fieldErrors.initial_earning}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                  <InputLabel htmlFor="duration">Duration</InputLabel>
                  <OutlinedInput
                    id="duration"
                    name="duration"
                    label="Duration"
                    multiline
                    minRows={2}
                    value={planForm.duration}
                    onChange={onChange}
                    error={Boolean(fieldErrors.duration)}
                  />
                  {fieldErrors.duration && (
                    <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                      {fieldErrors.duration}
                    </Typography>
                  )}
                </FormControl>

                {categories && categories.length > 0 && (
                  <FormControl fullWidth sx={{ m: 1, minWidth: 50 }}>
                    <InputLabel id="category-label">Select category</InputLabel>
                    <Select
                      labelId="category-label"
                      id="category"
                      name="category"
                      label="Select category"
                      value={planForm.category}
                      onChange={onChange}
                      disabled={categoriesLoading}
                      error={Boolean(fieldErrors.category)}
                    >
                      <MenuItem value="">
                        <em>Optional</em>
                      </MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={String(category.id)}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.category && (
                      <Typography color="error" sx={{ textAlign: 'left', fontSize: 14 }} variant="caption" display="block">
                        {fieldErrors.category}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </CardContent>

              <CardActions>
                <Button
                  onClick={onSubmit}
                  fullWidth
                  size="large"
                  color="primary"
                  variant="contained"
                  disabled={isSendingRequest || planFetching}
                >
                  {isSendingRequest ? 'Updating…' : 'Update Plan'}
                </Button>
              </CardActions>
            </Card>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default EditPlan;
